<script setup>
import { computed } from 'vue';
import { useWindowSize } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import wootConstants from 'dashboard/constants/globals';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import { getContactTierName } from 'dashboard/components-next/Contacts/contactTiers';

const props = defineProps({
  id: { type: Number, required: true },
  name: { type: String, default: '' },
  email: { type: String, default: '' },
  phoneNumber: { type: String, default: '' },
  tier: { type: String, default: '' },
  thumbnail: { type: String, default: '' },
  availabilityStatus: { type: String, default: null },
  selectable: { type: Boolean, default: false },
  isSelected: { type: Boolean, default: false },
});

const emit = defineEmits(['showContact', 'select', 'avatarHover']);

defineOptions({ inheritAttrs: false });

const { width: windowWidth } = useWindowSize();
const { t } = useI18n();

const isMobile = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const tierName = computed(() => getContactTierName(props.tier, t));

const onClickViewDetails = () => emit('showContact', props.id);

const toggleSelect = checked => {
  emit('select', checked);
};

const handleAvatarHover = isHovered => {
  emit('avatarHover', isHovered);
};

const onRowKeydown = event => {
  if (event.target !== event.currentTarget) return;
  event.preventDefault();
  onClickViewDetails();
};
</script>

<template>
  <div class="relative overflow-hidden">
    <div
      :key="id"
      role="button"
      tabindex="0"
      class="flex w-full items-center gap-3 px-1 py-3.5 text-left transition-colors hover:bg-n-alpha-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-n-brand md:px-2 md:py-4"
      :class="{
        'bg-n-brand/5': isSelected,
      }"
      @click="onClickViewDetails"
      @keydown.enter="onRowKeydown"
      @keydown.space="onRowKeydown"
    >
      <div
        class="relative shrink-0"
        @mouseenter="handleAvatarHover(true)"
        @mouseleave="handleAvatarHover(false)"
      >
        <Avatar
          :name="name"
          :src="thumbnail"
          :size="isMobile ? 36 : 40"
          :status="availabilityStatus"
          hide-offline-status
          rounded-full
        >
          <template v-if="selectable" #overlay="{ size }">
            <label
              class="absolute inset-0 z-10 flex items-center justify-center rounded-full cursor-pointer backdrop-blur-[2px] border border-n-weak"
              :style="{ width: `${size}px`, height: `${size}px` }"
              @click.stop
            >
              <Checkbox
                :model-value="isSelected"
                @change="event => toggleSelect(event.target.checked)"
              />
            </label>
          </template>
        </Avatar>
      </div>

      <span class="flex min-w-0 flex-1 flex-col gap-0.5">
        <span class="flex min-w-0 items-center gap-2">
          <span class="min-w-0 truncate text-sm font-medium text-n-slate-12">
            {{ name }}
          </span>
          <span
            v-if="tierName"
            class="shrink-0 rounded-full bg-n-amber-3 px-1.5 py-0.5 text-[10px] font-medium uppercase leading-none tracking-normal text-n-amber-11"
          >
            {{ tierName }}
          </span>
        </span>

        <span class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
          <span v-if="email" class="truncate text-xs text-n-slate-11">
            {{ email }}
          </span>
          <span
            v-if="email && phoneNumber"
            class="h-3 w-px shrink-0 rounded bg-n-slate-6"
          />
          <span v-if="phoneNumber" class="truncate text-xs text-n-slate-11">
            {{ phoneNumber }}
          </span>
        </span>
      </span>
    </div>
  </div>
</template>
